import {
  assistDomainAtom,
  assistLicensesAtom,
  auditLicensesAtom,
  availableAuditEntriesAtom,
  currentAuditEntryIdAtom,
  demoAuditEntry,
  issueContentLicensesAtom,
  translateEasyLicenses,
  userAtom,
} from '@/global-store';
import { isExpired } from '@helpers/isExpired';
import { api, demoEntry } from '@http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import { useURLSearchParam } from '@/hooks/useURLSearchParam';
import type {
  AuditData,
  Responsibility,
  TranslateData,
  WcagCheckerData,
  WebsiteData,
} from '@eye-able/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';

/** Which issue responsibilities (code / content / design) the org may see. */
export interface IssuesContentLicense {
  id: number;
  name: Responsibility;
}

/** Product modules the org may access, e.g. `plainLanguage`. */
export interface ModuleAccessLicense {
  id: number;
  name: string;
}

/**
 * `/listAllLicenses` returns one array per license type, always in this order.
 * `moduleAccesses` is not consumed here; the products read it via their own
 * queries.
 */
export type LicensesResponse = [
  assist: WebsiteData[],
  audit: WcagCheckerData[],
  report: AuditData[],
  translate: TranslateData[],
  issuesContent: IssuesContentLicense[],
  moduleAccesses: ModuleAccessLicense[],
];

export function useFetchLicenses() {
  const user = useAtomValue(userAtom);
  const [currentAuditEntryId, setCurrentAuditEntryId] = useAtom(
    currentAuditEntryIdAtom,
  );
  const [selectedAssistDomain, setSelectedAssistDomain] =
    useAtom(assistDomainAtom);

  const [availableAuditEntries, setAvailableAuditEntries] = useAtom(
    availableAuditEntriesAtom,
  );
  const setAuditLicenses = useSetAtom(auditLicensesAtom);
  const setAssistLicenses = useSetAtom(assistLicensesAtom);
  const setIssueContentLicenses = useSetAtom(issueContentLicensesAtom);
  const setTranslateEasyLicenses = useSetAtom(translateEasyLicenses);

  const { value: urlAuditEntryId } = useURLSearchParam('auditEntryId', {
    type: 'integer',
  });

  // Admin report-license responses are bounded, so retain an audit entry
  // selected from a deep link or persisted state when it falls outside the
  // default window. Capture the ids once so switching domains does not refetch
  // the bootstrap query.
  const includeAuditEntryIdsRef = useRef<string | null>(null);
  if (user && includeAuditEntryIdsRef.current === null) {
    includeAuditEntryIdsRef.current =
      user.isAdmin || user.isSalesAdmin
        ? Array.from(
            new Set(
              [urlAuditEntryId, currentAuditEntryId].filter(
                (auditEntryId): auditEntryId is number => auditEntryId !== null,
              ),
            ),
          ).join(',')
        : '';
  }
  const includeAuditEntryIds = includeAuditEntryIdsRef.current ?? '';

  const {
    data: licenses,
    isLoading,
    isError,
    error,
  } = useQuery({
    enabled: !!user,
    queryKey: [
      api.listAllLicenses,
      user?.id,
      user?.isAdmin,
      includeAuditEntryIds,
    ],
    queryFn: () =>
      fetchWrapper.get<LicensesResponse>(
        api.listAllLicenses,
        includeAuditEntryIds ? { includeAuditEntryIds } : undefined,
      ),
  });

  useEffect(() => {
    if (!user || isError || isLoading || !licenses) return;

    const [
      assistLicenses,
      auditLicenses,
      reportLicenses,
      translateLicenses,
      issuesContentLicenses,
    ] = licenses;

    setIssueContentLicenses(
      issuesContentLicenses.map((license) => license.name),
    );

    if (!reportLicenses.length) {
      setAvailableAuditEntries([demoAuditEntry]);
      setCurrentAuditEntryId(demoEntry.id);
    } else {
      setAvailableAuditEntries(reportLicenses);

      const hasSelectedEntry = reportLicenses.some(
        (entry) => entry.id === currentAuditEntryId,
      );

      if (!hasSelectedEntry) setCurrentAuditEntryId(reportLicenses[0].id);
    }

    const activeAssistLicenses = assistLicenses.filter(
      (license) => license.endDate !== null && !isExpired(license.endDate),
    );

    if (!activeAssistLicenses.length) {
      setAssistLicenses([]);
      setSelectedAssistDomain('demo-domain');
    } else {
      setAssistLicenses(assistLicenses);

      const hasSelectedDomain = activeAssistLicenses.some(
        (entry) => entry.domain === selectedAssistDomain,
      );

      if (!hasSelectedDomain)
        setSelectedAssistDomain(activeAssistLicenses[0].domain);
    }

    setAuditLicenses(auditLicenses);
    setTranslateEasyLicenses(translateLicenses);
  }, [
    licenses,
    isLoading,
    isError,
    user,
    setAssistLicenses,
    setAuditLicenses,
    selectedAssistDomain,
    setSelectedAssistDomain,
  ]);

  // The query is disabled until a user exists, so callers must treat a missing
  // user as "not ready" themselves (BaseRoute does, via isUserReady).
  // The store is written by the effect above, one commit after the query
  // settles; `isApplied` marks the moment the entries are actually in place.
  return {
    isLoading,
    isError,
    error,
    isApplied: availableAuditEntries.length > 0,
  };
}

export function useReloadLicenseData() {
  const queryClient = useQueryClient();

  return () =>
    queryClient.invalidateQueries({
      queryKey: [api.listAllLicenses],
    });
}
